import React from "react";
import {
  Box,
  Typography,
  TextField,
  FormHelperText,
  Autocomplete,
  Grid,
  Tooltip,
  Paper,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ModalTemplate from "./ModalTemplate";
import AppButton from "./AppButton";
import IconButtons from "./IconButtons";
import theme from "../../utils/theme";
import ImageUploadSection from "../CreateRFI/ImageUploadSection";
import useCreatePaintingTemplateContent from "../../hooks/useCreatePaintingTemplateContent";
import { InfoOutlined } from "@mui/icons-material";

/**
 * CreateUpdatePaintingTemplateForm
 * Responsibilities:
 * - Renders painting template modal UI for create/edit flows using ModalTemplate.
 * - Create mode: shows Save/Close actions; all updates are local until submit.
 * - Edit mode: fields auto-save via debounced calls from the hook; actions are hidden
 *   unless there are any new (unsaved) parts/subparts detected by `checkNewParts`
 *   and `checkNewSubparts`.
 * - Model selector disables options with `is_template_created` and is disabled entirely in edit mode.
 * - Footer note indicates auto-save behavior only when editing persisted entities (no new parts/subparts pending).
 */
const styles = {
  textField: {
    bgcolor: theme.palette.background.paper,
    "& .MuiInputBase-input": {
      height: 24,
      padding: "8px 12px",
    },
  },
  componentNameLabel: {
    fontWeight: 700,
    color: theme.palette.text.primary,
    fontSize: 16,
    width: "fit-content",
    marginRight: "16px",
  },
  dimensionsTitle: {
    fontWeight: 700,
    color: theme.palette.primary.main,
    fontSize: 18,
    letterSpacing: 0.1,
    textAlign: "left",
    mr: 2,
  },
  divider: {
    display: "block",
    borderBottom: `1.5px solid ${theme.palette.grey[500]}`,
    width: "100%",
  },
  itemGroupContainer: {
    mb: 3,
    position: "relative",
    bgcolor: theme.palette.background.default,
    borderRadius: 2,
    border: `1.5px solid ${theme.palette.grey[500]}`,
    boxShadow: 0,
    p: 2,
  },
  itemLabel: {
    fontWeight: 600,
    mb: 0.5,
  },
  itemTextField: {
    bgcolor: theme.palette.background.paper,
    width: 155,
    minWidth: 155,
    maxWidth: 155,
    "& .MuiInputBase-input": {
      height: 24,
      padding: "4px 8px",
    },
    mt: 0.5,
  },
  errorText: {
    color: theme.palette.error.main,
    fontSize: "0.75rem",
    mt: 0.5,
    ml: 1.5,
  },
  selectField: {
    bgcolor: theme.palette.background.paper,
    "& .MuiInputBase-input": {
      height: 24,
      padding: "8px 12px",
    },
    width: "100%",
  },
};

const CreateUpdatePaintingTemplateForm = ({
  open,
  onClose,
  initialData,
  onSave,
  isEditing = false,
  updateFromStage = false,
}) => {
  const {
    formik,
    isSubmitting,
    models,
    handleClose,
    handleModalChange,
    handleImageUpload,
    removeImage,
    addItem,
    removeItem,
    addSubPart,
    removeSubPart,
    debouncePartUpdate,
    debounceSubpartUpdate,
    checkNewParts,
    checkNewSubparts,
  } = useCreatePaintingTemplateContent(
    isEditing,
    onClose,
    initialData,
    onSave,
    open,
    updateFromStage
  );

  const renderActions = () => {
    return (
      <>
        <AppButton
          buttonType="greyed"
          onClick={handleClose}
          sx={{ mx: 1 }}
          label="Close"
        />
        <AppButton
          buttonType="primary"
          onClick={formik.handleSubmit}
          sx={{ minWidth: 120, mx: 1 }}
          label={isEditing ? "Update" : "Save"}
          isLoading={isSubmitting}
        />
      </>
    );
  };
  const PaperComponent = (props) => (
    <Paper {...props}>
      <Box
        sx={{
          p: 1,
          bgcolor: "grey.100",
          borderBottom: "1px solid",
          borderColor: "divider",
          display: "flex",
          alignItems: "center",
          gap: 0.5,
        }}
      >
        <InfoOutlined fontSize="small" color="error" />
        <Typography variant="caption" color="error">
          All models are assigned to a template.
        </Typography>
      </Box>
      {props.children}
    </Paper>
  );

  return (
    <ModalTemplate
      open={open}
      onClose={handleClose}
      title={isEditing ? "Edit Painting Template" : "Create Painting Template"}
      titleIcon={<AddIcon />}
      actions={renderActions()}
      maxWidth="md"
      showActions={!isEditing || checkNewParts || checkNewSubparts}
      footerNote={
        isEditing && !checkNewParts && !checkNewSubparts
          ? "Updates made to existing parts are saved automatically."
          : null
      }
    >
      <Box>
        {/* Model Dropdown Section - hide when updating from stage */}
        {!updateFromStage && (
          <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
            <Typography variant="subtitle1" sx={styles.componentNameLabel}>
              Model:
            </Typography>
            <Box sx={{ flex: 1 }}>
              <Autocomplete
                options={models}
                getOptionLabel={(option) => option.model_name}
                getOptionDisabled={(option) =>
                  Boolean(option?.is_template_created)
                }
                value={
                  models?.find(
                    (modal) => modal?.model_id === formik.values.model_id
                  ) || null
                }
                onChange={handleModalChange}
                PaperComponent={
                  models?.filter((md) => !md?.is_template_created).length > 0
                    ? null
                    : PaperComponent
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    size="small"
                    variant="outlined"
                    sx={styles.selectField}
                    error={Boolean(formik.errors.model_id)}
                    helperText={formik.errors.model_id}
                    required
                  />
                )}
                disabled={isEditing}
                isOptionEqualToValue={(option, value) =>
                  option.model_id === value.model_id
                }
              />
            </Box>
          </Box>
        )}

        {/* Image Upload Section */}
        <Box sx={{ mb: 2 }}>
          <ImageUploadSection
            images={formik.values.painting_checklist_template_images || []}
            handleImageUpload={handleImageUpload}
            handleRemoveImage={removeImage}
            error={
              formik.submitCount > 0
                ? formik.errors.painting_checklist_template_images
                : undefined
            }
          />
        </Box>

        {/* Template Parts */}
        <Box sx={{ display: "flex", alignItems: "center", mt: 2, mb: 1 }}>
          <Typography variant="subtitle1" sx={styles.dimensionsTitle}>
            Template Parts
          </Typography>
          <Box sx={{ flex: 1, ml: 1 }}>
            <span style={styles.divider} />
          </Box>
        </Box>

        <Box sx={{ mt: 3, mb: 2 }}>
          {formik.values.painting_checklist_template_parts &&
            formik.values.painting_checklist_template_parts.map(
              (item, itemIdx) => (
                <Box key={itemIdx} sx={styles.itemGroupContainer}>
                  {formik.values.painting_checklist_template_parts.length >
                    1 && (
                    <Tooltip title="Delete Item" arrow placement="top">
                      <IconButtons
                        buttonType="close"
                        onClick={() => removeItem(itemIdx)}
                        sx={{ float: "right" }}
                      />
                    </Tooltip>
                  )}

                  <Grid container spacing={2} mt={2}>
                    {/* Part Number - displays item index + 1 as serial number */}
                    <Grid item size={{ xs: 12, md: 1 }}>
                      <Typography variant="body2" sx={styles.itemLabel}>
                        Part No
                      </Typography>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          ml: 1,
                          p: 1,
                        }}
                      >
                        <Typography variant="body1" sx={styles.itemLabel}>
                          {itemIdx + 1}
                        </Typography>
                      </Box>
                    </Grid>

                    {/* Part Name */}
                    <Grid item size={{ xs: 12, md: 2 }}>
                      <Typography variant="body2" sx={styles.itemLabel}>
                        Part Area
                      </Typography>
                      <Box>
                        <TextField
                          size="small"
                          name={`painting_checklist_template_parts[${itemIdx}].part_name`}
                          value={item.part_name || ""}
                          onChange={(e) => {
                            formik.handleChange(e);
                            if (
                              isEditing &&
                              formik.values.painting_checklist_template_parts?.[
                                itemIdx
                              ]?.painting_checklist_part_id
                            ) {
                              debouncePartUpdate(
                                itemIdx,
                                "part_name",
                                e.target.value
                              );
                            }
                          }}
                          variant="outlined"
                          placeholder=""
                          sx={{
                            ...styles.itemTextField,
                            width: "100%",
                            minWidth: "auto",
                            maxWidth: "none",
                          }}
                          fullWidth
                          error={
                            formik.touched.painting_checklist_template_parts?.[
                              itemIdx
                            ]?.part_name &&
                            Boolean(
                              formik.errors.painting_checklist_template_parts?.[
                                itemIdx
                              ]?.part_name
                            )
                          }
                          required
                        />
                        {formik.touched.painting_checklist_template_parts?.[
                          itemIdx
                        ]?.part_name &&
                          formik.errors.painting_checklist_template_parts?.[
                            itemIdx
                          ]?.part_name && (
                            <FormHelperText sx={styles.errorText}>
                              {
                                formik.errors
                                  .painting_checklist_template_parts?.[itemIdx]
                                  ?.part_name
                              }
                            </FormHelperText>
                          )}
                      </Box>
                    </Grid>

                    {/* Part Location */}
                    <Grid item size={{ xs: 12, md: 2 }}>
                      <Typography variant="body2" sx={styles.itemLabel}>
                        Part Location
                      </Typography>
                      <Box>
                        <TextField
                          size="small"
                          name={`painting_checklist_template_parts[${itemIdx}].part_location`}
                          value={item.part_location || ""}
                          onChange={(e) => {
                            formik.handleChange(e);
                            if (
                              isEditing &&
                              formik.values.painting_checklist_template_parts?.[
                                itemIdx
                              ]?.painting_checklist_part_id
                            ) {
                              debouncePartUpdate(
                                itemIdx,
                                "part_location",
                                e.target.value
                              );
                            }
                          }}
                          variant="outlined"
                          placeholder=""
                          sx={{
                            ...styles.itemTextField,
                            width: "100%",
                            minWidth: "auto",
                            maxWidth: "none",
                          }}
                          fullWidth
                          error={
                            formik.touched.painting_checklist_template_parts?.[
                              itemIdx
                            ]?.part_location &&
                            Boolean(
                              formik.errors.painting_checklist_template_parts?.[
                                itemIdx
                              ]?.part_location
                            )
                          }
                          required
                        />
                        {formik.touched.painting_checklist_template_parts?.[
                          itemIdx
                        ]?.part_location &&
                          formik.errors.painting_checklist_template_parts?.[
                            itemIdx
                          ]?.part_location && (
                            <FormHelperText sx={styles.errorText}>
                              {
                                formik.errors
                                  .painting_checklist_template_parts?.[itemIdx]
                                  ?.part_location
                              }
                            </FormHelperText>
                          )}
                      </Box>
                    </Grid>

                    <Grid item size={{ xs: 12, md: 7 }}>
                      <Typography variant="body2" sx={styles.itemLabel}>
                        Item Part Inspection
                      </Typography>
                      {item.painting_part_items &&
                        item.painting_part_items.map((subPart, subPartIdx) => (
                          <React.Fragment key={subPartIdx}>
                            <Box
                              display="flex"
                              alignItems="center"
                              gap={1}
                              className="subpart-row-hover"
                              mt={0.5}
                            >
                              <TextField
                                size="small"
                                name={`painting_checklist_template_parts[${itemIdx}].painting_part_items[${subPartIdx}].item_name`}
                                value={subPart.item_name || ""}
                                onChange={(e) => {
                                  formik.handleChange(e);
                                  const subId =
                                    formik.values
                                      .painting_checklist_template_parts?.[
                                      itemIdx
                                    ]?.painting_part_items?.[subPartIdx]
                                      ?.painting_checklist_part_item_id;
                                  if (isEditing && subId) {
                                    debounceSubpartUpdate(
                                      itemIdx,
                                      subPartIdx,
                                      e.target.value
                                    );
                                  }
                                }}
                                variant="outlined"
                                placeholder=""
                                sx={{
                                  ...styles.itemTextField,
                                  width: "100%",
                                  minWidth: "auto",
                                  maxWidth: "none",
                                  flex: 1,
                                }}
                                fullWidth
                                error={
                                  formik.touched
                                    .painting_checklist_template_parts?.[
                                    itemIdx
                                  ]?.painting_part_items?.[subPartIdx]
                                    ?.item_name &&
                                  Boolean(
                                    formik.errors
                                      .painting_checklist_template_parts?.[
                                      itemIdx
                                    ]?.painting_part_items?.[subPartIdx]
                                      ?.item_name
                                  )
                                }
                              />
                              {item.painting_part_items.length > 1 && (
                                <span className="subpart-delete-icon">
                                  <Tooltip
                                    title="Delete Part"
                                    arrow
                                    placement="top"
                                  >
                                    <IconButtons
                                      buttonType="delete"
                                      onClick={() =>
                                        removeSubPart(itemIdx, subPartIdx)
                                      }
                                    />
                                  </Tooltip>
                                </span>
                              )}
                            </Box>
                            {formik.touched.painting_checklist_template_parts?.[
                              itemIdx
                            ]?.painting_part_items?.[subPartIdx]?.item_name &&
                              formik.errors.painting_checklist_template_parts?.[
                                itemIdx
                              ]?.painting_part_items?.[subPartIdx]
                                ?.item_name && (
                                <FormHelperText sx={styles.errorText}>
                                  {
                                    formik.errors
                                      .painting_checklist_template_parts?.[
                                      itemIdx
                                    ]?.painting_part_items?.[subPartIdx]
                                      ?.item_name
                                  }
                                </FormHelperText>
                              )}
                          </React.Fragment>
                        ))}

                      <Box
                        display="flex"
                        justifyContent="center"
                        alignItems="center"
                        sx={{
                          mt: 2,
                          mb: 1,
                          width: "100%",
                        }}
                      >
                        <Tooltip title="Add" arrow placement="top">
                          <IconButtons
                            buttonType="add"
                            onClick={() => addSubPart(itemIdx)}
                          />
                        </Tooltip>
                      </Box>
                    </Grid>
                  </Grid>
                </Box>
              )
            )}

          {/* Add Item Button */}
          <Box display="flex" justifyContent="flex-end" sx={{ mt: 2, mb: 1 }}>
            <AppButton
              buttonType="primary"
              onClick={addItem}
              startIcon={<AddIcon sx={{ color: "#fff" }} />}
              label="Add Part"
            />
          </Box>
        </Box>
      </Box>
    </ModalTemplate>
  );
};

export default CreateUpdatePaintingTemplateForm;
